import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Play, Plus, Webhook, Workflow as WorkflowIcon } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { useAuth } from "../lib/auth";
import { listWorkflows } from "../lib/api";
import type { Workflow } from "../lib/api";
import { cn } from "../lib/cn";

function TriggerIcon({ type }: { type: string }) {
  if (type === "schedule") return <Clock className="h-4 w-4" />;
  if (type === "webhook") return <Webhook className="h-4 w-4" />;
  return <Play className="h-4 w-4" />;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "never run";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

const newButton = (
  <Link
    to="/app/workflows/new"
    className="inline-flex h-10 items-center gap-2 rounded-xl bg-brand-gradient px-4 text-sm font-semibold text-white shadow-lift"
  >
    <Plus className="h-4 w-4" /> New workflow
  </Link>
);

export default function Workflows() {
  const { currentTenant } = useAuth();
  const tenant = currentTenant?.slug ?? "";
  const [rows, setRows] = useState<Workflow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant) return;
    setRows(null);
    listWorkflows(tenant)
      .then(setRows)
      .catch(() => setError("Couldn't load workflows."));
  }, [tenant]);

  return (
    <>
      <PageHeader
        title="Workflows"
        description="Chain connector operations into automated jobs. Build one step by step — every connector speaks the same five verbs, so any two systems compose."
        actions={newButton}
      />

      {rows === null && !error ? (
        <div className="flex justify-center py-20">
          <Spinner className="h-8 w-8 text-brand-500" />
        </div>
      ) : error ? (
        <Card className="px-6 py-16 text-center">
          <p className="font-semibold text-slate-800">{error}</p>
        </Card>
      ) : rows && rows.length === 0 ? (
        <EmptyState
          icon={<WorkflowIcon className="h-6 w-6" />}
          title="No workflows yet"
          description="Build your first automation — pick a trigger, then chain connector operations step by step."
          action={newButton}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {rows!.map((w) => (
            <Link
              key={w.id}
              to={`/app/workflows/${w.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-card transition-all hover:-translate-y-1 hover:shadow-lift"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100">
                  <WorkflowIcon className="h-5 w-5" />
                </span>
                {!w.enabled && <Badge tone="slate">Paused</Badge>}
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{w.name}</h3>
              <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-slate-500">
                {w.description || "No description."}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-4 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5 text-slate-500">
                  <TriggerIcon type={w.trigger_type} /> {w.trigger_summary || w.trigger_type}
                </span>
                <span>
                  {w.step_count} step{w.step_count === 1 ? "" : "s"}
                </span>
                {w.last_run_status && (
                  <span className="inline-flex items-center gap-1">
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        w.last_run_status === "succeeded" && "bg-emerald-500",
                        w.last_run_status === "failed" && "bg-rose-500",
                        w.last_run_status !== "succeeded" &&
                          w.last_run_status !== "failed" &&
                          "bg-slate-300",
                      )}
                    />
                    {timeAgo(w.last_run_at)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
